<script setup lang="ts">
import { computed, ref } from "vue";
import QuantityStepper from "@/components/QuantityStepper.vue";
import type { TicketType } from "@/types";

const props = defineProps<{
  ticket: TicketType;
  quantity: number;
  formatPrice: (value: number) => string;
}>();

const emit = defineEmits<{
  quantityChange: [ticket: TicketType, quantity: number];
}>();

const isDescriptionOpen = ref(false);
const hasDescription = computed(() => Boolean(props.ticket.description.trim()));
const descriptionId = computed(() => `ticket-description-${props.ticket.id}`);

function toggleDescription() {
  if (!hasDescription.value) {
    return;
  }

  isDescriptionOpen.value = !isDescriptionOpen.value;
}

function updateQuantity(quantity: number) {
  emit("quantityChange", props.ticket, quantity);
}
</script>

<template>
  <article
    class="rounded-[24px] lg:rounded-[40px] bg-white p-4 lg:p-8 shadow-widget"
  >
    <div class="flex gap-2 lg:gap-6 justify-between">
      <div class="min-w-0">
        <p
          class="mb-1 lg:mb-2 text-[10px] lg:text-base font-medium text-ink/50"
        >
          Тариф
        </p>
        <h2 class="m-0">
          <button
            class="grid w-full grid-cols-[minmax(0,1fr)_20px] items-start gap-1 text-left text-sm leading-[18px] font-semibold uppercase tracking-normal text-brand lg:w-auto lg:grid-cols-[minmax(0,1fr)_24px] lg:items-center lg:gap-2 lg:text-2xl lg:leading-7"
            :class="hasDescription ? 'cursor-pointer' : 'cursor-default'"
            type="button"
            :aria-controls="descriptionId"
            :aria-expanded="isDescriptionOpen"
            :disabled="!hasDescription"
            @click="toggleDescription"
          >
            <span class="min-w-0">{{ ticket.title }}</span>
            <svg
              v-if="hasDescription"
              class="h-5 w-5 min-w-5 transition-transform duration-200 lg:h-6 lg:w-6 lg:min-w-6"
              :class="isDescriptionOpen ? 'rotate-90' : '-rotate-90'"
              aria-hidden="true"
            >
              <use href="#ticket-widget-icon-arrow-left" />
            </svg>
          </button>
        </h2>
        <p
          class="mt-1 lg:mt-3 text-base leading-5 lg:text-2xl lg:leading-7 font-bold text-ink"
        >
          {{ formatPrice(ticket.price) }}
        </p>
      </div>

      <div class="self-start text-center">
        <p
          class="mb-1 lg:mb-2 text-[10px] lg:text-base font-medium text-ink/50"
        >
          Выбрать количество
        </p>
        <QuantityStepper
          :model-value="quantity"
          @update:model-value="updateQuantity"
        />
      </div>
    </div>

    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="-translate-y-1 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="-translate-y-1 opacity-0"
    >
      <div
        v-if="isDescriptionOpen"
        :id="descriptionId"
        class="mt-4 lg:mt-5 border-t border-ink/10 pt-4 lg:pt-5"
      >
        <p
          class="text-sm lg:text-base font-medium leading-[18px] lg:leading-5 text-ink/80"
        >
          {{ ticket.description }}
        </p>
      </div>
    </Transition>
  </article>
</template>
